import { DynamicBaseUrlType, DynamicUrlField } from '../constants/urlPaths';
import { setError } from '../redux';
import type { AppDispatch } from '../redux/store';
import { resolveDynamicUrl } from './appUtils';

/**
 * Checks if user has a specific permission
 * @param userPermissionList - Array of user permissions
 * @param requiredPermission - Permission to check for
 * @returns true if user has permission, false otherwise
 */
export const hasPermission = (
  userPermissionList: string[] | undefined,
  requiredPermission: string,
): boolean => {
  return userPermissionList?.includes(requiredPermission) || false;
};

/**
 * Checks if user has required permission and dispatches error if not
 * @param userPermissionList - Array of user permissions
 * @param requiredPermission - Permission to check for
 * @param dispatch - Redux dispatch function
 * @param errorMessage - Optional custom error message
 * @returns true if user has permission, false otherwise
 */
export const checkPermissionWithError = (
  userPermissionList: string[] | undefined,
  requiredPermission: string,
  dispatch: AppDispatch,
  errorMessage = 'You do not have the required permissions to access this feature.',
): boolean => {
  const permitted = hasPermission(userPermissionList, requiredPermission);

  if (!permitted) {
    dispatch(
      setError({
        data: errorMessage,
        status: 403,
      }),
    );
  }

  return permitted;
};

/**
 * Handles external navigation with permission check
 * Opens URL in new tab if user has required permission, otherwise shows error
 *
 * @param userPermissionList - Array of user permissions
 * @param permission - Required permission to check
 * @param url - URL to navigate to
 * @param dispatch - Redux dispatch function
 *
 * @example
 * handleExternalNavigation(userPermissions, 'Marketing_Tool_Access', 'https://example.com', dispatch)
 */
export const handleExternalNavigation = (
  userPermissionList: string[] | undefined,
  permission: string,
  url: string,
  dispatch: AppDispatch,
  buildDynamicUrl: (
    baseUrlType: DynamicBaseUrlType,
    urlCode: string,
    field?: DynamicUrlField,
  ) => string | null,
  urlCode?: string,
): void => {
  if (!checkPermissionWithError(userPermissionList, permission, dispatch)) {
    return;
  }
  const resolvedUrl = resolveDynamicUrl(url, urlCode, buildDynamicUrl);
  if (resolvedUrl) {
    window.open(resolvedUrl, '_blank');
  }
};
